const DEFAULT_PRODUCT_IMAGE = `${process.env.PUBLIC_URL}/images/jewellery-img5.jpg`;

const ensureStringArray = (value, fallback) => {
  if (!Array.isArray(value)) {
    return fallback;
  }

  const cleaned = value.filter(Boolean);
  return cleaned.length ? cleaned : fallback;
};

const ensureNumberArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(Number)
    .filter((item) => Number.isFinite(item) && item >= 0);
};

export const slugifyCategoryName = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const getPrimaryBadge = (product = {}) => {
  if (typeof product.badge === 'string' && product.badge.trim()) {
    return product.badge.trim();
  }

  if (Array.isArray(product.badges)) {
    const firstBadge = product.badges.find((badge) => typeof badge === 'string' && badge.trim());
    return firstBadge ? firstBadge.trim() : '';
  }

  return '';
};

export const hasProductBadge = (product = {}, badgeValue = '') => {
  const normalizedTarget = String(badgeValue).trim().toLowerCase();
  if (!normalizedTarget) {
    return false;
  }

  return [getPrimaryBadge(product), ...(Array.isArray(product.badges) ? product.badges : [])]
    .filter(Boolean)
    .some((badge) => String(badge).trim().toLowerCase() === normalizedTarget);
};

export const getProductTimestamp = (product = {}) => {
  const parsed = Date.parse(product.createdAt || product.updatedAt || '');
  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  const numericId = Number(product.id);
  return Number.isFinite(numericId) ? numericId : 0;
};

export const normalizeProduct = (product = {}) => {
  const prices = ensureNumberArray(product.prices);
  const offerPrices = ensureNumberArray(product.offerPrices);
  const basePrice = prices[0] ?? 0;
  const offerPrice = offerPrices[0];
  const price = typeof product.price === 'number' ? product.price : offerPrice ?? basePrice;
  const originalPrice = typeof product.originalPrice === 'number'
    ? product.originalPrice
    : offerPrice !== undefined ? basePrice : price;
  const sourceImages =
    product.images && typeof product.images === 'object' && !Array.isArray(product.images)
      ? product.images
      : {};
  const image = product.image || sourceImages.front || DEFAULT_PRODUCT_IMAGE;
  const badge = getPrimaryBadge(product);
  const badges =
    Array.isArray(product.badges) && product.badges.length
      ? product.badges.filter(Boolean)
      : badge
        ? [badge]
        : [];

  return {
    ...product,
    id: product.id || product._id || '',
    image,
    images: {
      front: sourceImages.front || image,
      right: sourceImages.right || '',
      left: sourceImages.left || '',
      top: sourceImages.top || '',
      detail: sourceImages.detail || '',
    },
    category: product.category || 'Unknown',
    subcategory: product.subcategory || '',
    colors: ensureStringArray(product.colors, ['N/A']),
    materials: ensureStringArray(product.materials, ['N/A']),
    sizes: ensureStringArray(product.sizes, ['One Size']),
    price,
    prices,
    offerPrices,
    features: ensureStringArray(product.features, []),
    specifications:
      product.specifications && typeof product.specifications === 'object'
        ? product.specifications
        : {},
    originalPrice,
    badge,
    badges,
    rating: typeof product.rating === 'number' ? product.rating : 0,
    reviews: typeof product.reviews === 'number' ? product.reviews : 0,
    stock: typeof product.stock === 'number' ? product.stock : 0,
    trending: Boolean(product.trending),
    featuredOnHome: Boolean(product.featuredOnHome),
    shippingClass: product.shippingClass || 'Standard',
    shippingFee:
      typeof product.shippingFee === 'number' && product.shippingFee >= 0
        ? product.shippingFee
        : 0,
    returnDays: typeof product.returnDays === 'number' ? product.returnDays : 7,
    status: product.status || 'Active',
    createdAt: product.createdAt || null,
    updatedAt: product.updatedAt || null,
  };
};

const DEFAULT_CATEGORY_ICON = 'fa-tag';
const CATEGORY_ICON_BY_SLUG = {
  jewelry: 'fa-gem',
  'hair-care': 'fa-spa',
  fashion: 'fa-shirt',
  'home-gifts': 'fa-house',
};

export const normalizeCategory = (category, index) => {
  const name = category.name || category.category || 'Unknown';
  const slug = category.slug || slugifyCategoryName(name);

  return {
    id: category.id || category._id || `category-${index + 1}`,
    name,
    slug,
    description: category.description || '',
    image: category.image || '',
    icon: category.icon || CATEGORY_ICON_BY_SLUG[slug] || DEFAULT_CATEGORY_ICON,
    subcategories: Array.isArray(category.subcategories)
      ? category.subcategories.filter(Boolean)
      : [],
    count: typeof category.count === 'number' ? category.count : 0,
  };
};

export const buildCategoriesFromProducts = (items = []) => {
  const grouped = items.reduce((acc, product) => {
    const name = product.category || 'Unknown';
    const key = name.toLowerCase();

    if (!acc[key]) {
      const slug = slugifyCategoryName(name);
      acc[key] = {
        id: `derived-${key}`,
        name,
        slug,
        icon: CATEGORY_ICON_BY_SLUG[slug] || DEFAULT_CATEGORY_ICON,
        subcategories: new Set(),
        count: 0,
      };
    }

    if (product.subcategory) {
      acc[key].subcategories.add(product.subcategory);
    }

    acc[key].count += 1;
    return acc;
  }, {});

  return Object.values(grouped).map((category) => ({
    ...category,
    subcategories: [...category.subcategories],
  }));
};
